// AdminNotificationPanel.tsx
// Place in: src/components/AdminNotificationPanel.tsx

import React, { useState, useEffect } from 'react';
import {
  Bell, Send, Users, User, Calendar, Video, Plane,
  CreditCard, FileText, Building2, Megaphone, Clock,
  Trash2, RefreshCw, Smartphone, CheckCircle, Zap,
  Image, Plus, MapPin, Home, DollarSign, BedDouble, Eye,
  Mail, Loader
} from 'lucide-react';
import { useLanguage } from './LanguageContext';

interface NotificationFormData {
  type: 'payment' | 'document' | 'construction' | 'event' | 'webinar' | 'journey' | 'announcement' | 'reminder';
  title: string;
  titleDe: string;
  message: string;
  messageDe: string;
  priority: 'low' | 'medium' | 'high';
  targetAudience: 'all' | 'specific';
  investorId: string;
  actionUrl: string;
  actionType: 'internal' | 'external' | 'none';
}

interface Notification {
  id: string;
  investorId: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  priority: string;
}

interface AdminNotificationPanelProps {
  apiUrl: string;
  investors?: { id: string; name: string }[];
}

// `value` is the type sent to the backend — never translated. `labelKey` is the
// translation key for the button caption (module scope, so t() is applied at render).
const notificationTypes = [
  { value: 'announcement', labelKey: 'anpTypeAnnouncement', icon: Megaphone, color: '#D6B160' },
  { value: 'webinar', labelKey: 'anpTypeWebinar', icon: Video, color: '#4A90D9' },
  { value: 'journey', labelKey: 'anpTypeJourney', icon: Plane, color: '#50C878' },
  { value: 'event', labelKey: 'anpTypeEvent', icon: Calendar, color: '#9B59B6' },
  { value: 'payment', labelKey: 'anpTypePayment', icon: CreditCard, color: '#E74C3C' },
  { value: 'document', labelKey: 'anpTypeDocument', icon: FileText, color: '#3498DB' },
  { value: 'construction', labelKey: 'anpTypeConstruction', icon: Building2, color: '#F39C12' },
  { value: 'reminder', labelKey: 'anpTypeReminder', icon: Clock, color: '#95A5A6' },
];

interface AdminStory {
  id: string;
  title: string;
  titleDe?: string;
  imageUrl: string;
  description?: string;
  descriptionDe?: string;
  linkLabel?: string;
  linkUrl?: string;
  category: string;
  project?: string;
  price?: string;
  bedrooms?: string;
  location?: string;
  viewCount?: number;
  createdAt: any;
  expiresAt?: any;
}

// Priority values stay 'low' | 'medium' | 'high' on the wire; only the caption
// is translated.
const priorityLabelKeys: Record<'low' | 'medium' | 'high', string> = {
  low: 'anpPriorityLow',
  medium: 'anpPriorityMedium',
  high: 'anpPriorityHigh',
};

// `value` is the category stored with the story — never translated.
const storyCategoryOptions = [
  { value: 'listing', labelKey: 'anpCatListing', icon: Home, color: '#D6B160' },
  { value: 'unit', labelKey: 'anpCatUnit', icon: Building2, color: '#4A90D9' },
  { value: 'offer', labelKey: 'anpCatOffer', icon: Zap, color: '#E74C3C' },
  { value: 'update', labelKey: 'anpCatUpdate', icon: Megaphone, color: '#50C878' },
  { value: 'promo', labelKey: 'anpCatFeatured', icon: CheckCircle, color: '#9B59B6' },
];

const AdminNotificationPanel: React.FC<AdminNotificationPanelProps> = ({ apiUrl, investors = [] }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'create' | 'history' | 'app-update' | 'stories' | 'portfolio-email'>('create');

  // Portfolio Email state
  const [portfolioEmailInvestorId, setPortfolioEmailInvestorId] = useState('');
  const [portfolioEmailPreview, setPortfolioEmailPreview] = useState('');
  const [portfolioEmailLoading, setPortfolioEmailLoading] = useState(false);
  const [portfolioEmailSending, setPortfolioEmailSending] = useState(false);
  const [portfolioEmailBulkSending, setPortfolioEmailBulkSending] = useState(false);
  const [portfolioEmailStatus, setPortfolioEmailStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [portfolioEmailSummary, setPortfolioEmailSummary] = useState<{ totalUnits: number; totalValue: number; totalROI: number } | null>(null);
  const [portfolioInvestorList, setPortfolioInvestorList] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentNotifications, setRecentNotifications] = useState<Notification[]>([]);

  // Stories tab state
  const [storyLoading, setStoryLoading] = useState(false);
  const [storySuccess, setStorySuccess] = useState<string | null>(null);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [existingStories, setExistingStories] = useState<AdminStory[]>([]);
  const [storyForm, setStoryForm] = useState({
    title: '',
    titleDe: '',
    imageUrl: '',
    description: '',
    descriptionDe: '',
    linkLabel: '',
    linkUrl: '',
    category: 'listing',
    project: '',
    price: '',
    bedrooms: '',
    location: '',
    expiresInHours: '168', // 7 days default
  });

  const loadStories = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/stories/admin`);
      if (response.ok) {
        const data = await response.json();
        setExistingStories(data.stories || []);
      }
    } catch (err) {
      console.error('Failed to load stories:', err);
    }
  };

  const handleCreateStory = async () => {
    if (!storyForm.title || !storyForm.imageUrl) {
      setStoryError(t('anpStoryFieldsRequired'));
      return;
    }
    setStoryLoading(true);
    setStoryError(null);
    setStorySuccess(null);

    try {
      const response = await fetch(`${apiUrl}/api/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...storyForm,
          expiresInHours: parseInt(storyForm.expiresInHours) || 168,
        }),
      });

      if (response.ok) {
        setStorySuccess(t('anpStoryPublished'));
        setStoryForm({
          title: '', titleDe: '', imageUrl: '', description: '', descriptionDe: '',
          linkLabel: '', linkUrl: '', category: 'listing', project: '', price: '',
          bedrooms: '', location: '', expiresInHours: '168',
        });
        loadStories();
      } else {
        throw new Error(t('anpStoryCreateFailed'));
      }
    } catch (err) {
      setStoryError(err instanceof Error ? err.message : t('anpStoryCreateFailed'));
    } finally {
      setStoryLoading(false);
    }
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!window.confirm(t('anpDeleteStoryConfirm'))) return;
    try {
      const response = await fetch(`${apiUrl}/api/stories/${storyId}`, { method: 'DELETE' });
      if (response.ok) {
        setExistingStories(prev => prev.filter(s => s.id !== storyId));
      }
    } catch (err) {
      console.error('Failed to delete story:', err);
    }
  };

  // App Update tab state
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateForm, setUpdateForm] = useState({
    version: '',
    titleEn: 'New App Update Available!',
    titleDe: 'Neues App-Update verfügbar!',
    features: ['', '', ''],
    messageEn: '',
    messageDe: '',
  });

  const handleSendAppUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(null);

    const featuresEn = updateForm.features.filter(f => f.trim()).map(f => `• ${f}`).join('\n');
    const fullMessageEn = updateForm.messageEn
      ? updateForm.messageEn
      : `We're excited to announce ${updateForm.version ? `version ${updateForm.version} of ` : 'an update to '}the Asset Desk app!\n\n${featuresEn ? `What's New:\n${featuresEn}\n\n` : ''}Update now for the best experience.`;
    const fullMessageDe = updateForm.messageDe
      ? updateForm.messageDe
      : `Wir freuen uns, ${updateForm.version ? `Version ${updateForm.version} der ` : 'ein Update der '}Asset Desk App anzukündigen!\n\n${featuresEn ? `Was ist neu:\n${featuresEn}\n\n` : ''}Aktualisieren Sie jetzt für das beste Erlebnis.`;

    try {
      const response = await fetch(`${apiUrl}/api/notifications/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'announcement',
          title: updateForm.titleEn,
          titleDe: updateForm.titleDe,
          message: fullMessageEn,
          messageDe: fullMessageDe,
          priority: 'high',
          metadata: {
            isAppUpdate: true,
            version: updateForm.version,
            imageUrl: '/app-update-banner.png',
          },
        }),
      });

      if (response.ok) {
        setUpdateSuccess(t('anpUpdateSent'));
        setUpdateForm({
          version: '',
          titleEn: 'New App Update Available!',
          titleDe: 'Neues App-Update verfügbar!',
          features: ['', '', ''],
          messageEn: '',
          messageDe: '',
        });
      } else {
        throw new Error(t('anpUpdateSendFailed'));
      }
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : t('anpSendFailedShort'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const [formData, setFormData] = useState<NotificationFormData>({
    type: 'announcement',
    title: '',
    titleDe: '',
    message: '',
    messageDe: '',
    priority: 'medium',
    targetAudience: 'all',
    investorId: '',
    actionUrl: '',
    actionType: 'none'
  });

  const loadNotifications = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/notifications/all`);
      if (response.ok) {
        const data = await response.json();
        setRecentNotifications(data.slice(0, 20));
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadNotifications();
    }
    if (activeTab === 'stories') {
      loadStories();
    }
    if (activeTab === 'portfolio-email' && portfolioInvestorList.length === 0) {
      fetch(`${apiUrl}/api/portfolio-email/investors`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token') || ''}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) setPortfolioInvestorList(data.investors || []);
        })
        .catch(err => console.error('Failed to load investor list:', err));
    }
  }, [activeTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = formData.targetAudience === 'all' 
        ? `${apiUrl}/api/notifications/broadcast`
        : `${apiUrl}/api/notifications`;

      const body: Record<string, unknown> = {
        type: formData.type,
        title: formData.title,
        titleDe: formData.titleDe || formData.title,
        message: formData.message,
        messageDe: formData.messageDe || formData.message,
        priority: formData.priority,
      };

      if (formData.targetAudience === 'specific') {
        body.investorId = formData.investorId;
      }

      if (formData.actionType !== 'none' && formData.actionUrl) {
        body.actionUrl = formData.actionUrl;
        body.actionType = formData.actionType;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setSuccess(t('anpSentSuccess'));
        setFormData({
          type: 'announcement',
          title: '',
          titleDe: '',
          message: '',
          messageDe: '',
          priority: 'medium',
          targetAudience: 'all',
          investorId: '',
          actionUrl: '',
          actionType: 'none'
        });
      } else {
        throw new Error(t('anpSendFailed'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('anpSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (!window.confirm(t('anpDeleteNotificationConfirm'))) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setRecentNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Portfolio Email handlers
  const handlePortfolioEmailPreview = async () => {
    if (!portfolioEmailInvestorId) return;
    setPortfolioEmailLoading(true);
    setPortfolioEmailStatus(null);
    setPortfolioEmailPreview('');
    setPortfolioEmailSummary(null);
    try {
      const response = await fetch(`${apiUrl}/api/portfolio-email/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ investorId: portfolioEmailInvestorId })
      });
      const data = await response.json();
      if (data.success) {
        setPortfolioEmailPreview(data.html);
        setPortfolioEmailSummary(data.portfolioSummary);
        setPortfolioEmailStatus({ type: 'success', message: t('anpPePreviewOk') });
      } else {
        setPortfolioEmailStatus({ type: 'error', message: data.error || t('anpPePreviewFailed') });
      }
    } catch (err) {
      setPortfolioEmailStatus({ type: 'error', message: err instanceof Error ? err.message : t('anpPePreviewFailed') });
    } finally {
      setPortfolioEmailLoading(false);
    }
  };

  const handlePortfolioEmailSend = async () => {
    if (!portfolioEmailInvestorId) return;
    if (!window.confirm(t('anpPeSendConfirm'))) return;
    setPortfolioEmailSending(true);
    setPortfolioEmailStatus(null);
    try {
      const response = await fetch(`${apiUrl}/api/portfolio-email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ investorId: portfolioEmailInvestorId })
      });
      const data = await response.json();
      if (data.success) {
        setPortfolioEmailStatus({ type: 'success', message: data.message });
      } else {
        setPortfolioEmailStatus({ type: 'error', message: data.error || t('anpPeEmailFailed') });
      }
    } catch (err) {
      setPortfolioEmailStatus({ type: 'error', message: err instanceof Error ? err.message : t('anpPeEmailFailed') });
    } finally {
      setPortfolioEmailSending(false);
    }
  };

  const handlePortfolioEmailSendAll = async () => {
    if (!window.confirm(t('anpPeSendAllConfirm'))) return;
    setPortfolioEmailBulkSending(true);
    setPortfolioEmailStatus(null);
    try {
      const response = await fetch(`${apiUrl}/api/portfolio-email/send-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token') || ''}` }
      });
      const data = await response.json();
      if (data.success) {
        setPortfolioEmailStatus({ type: 'success', message: data.message });
      } else {
        setPortfolioEmailStatus({ type: 'error', message: data.error || t('anpPeBulkFailed') });
      }
    } catch (err) {
      setPortfolioEmailStatus({ type: 'error', message: err instanceof Error ? err.message : t('anpPeBulkFailed') });
    } finally {
      setPortfolioEmailBulkSending(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <Bell size={24} color="#D6B160" />
          <h1 style={styles.title}>{t('anpTitle')}</h1>
        </div>
        <div style={styles.tabs}>
          <button
            style={{...styles.tab, ...(activeTab === 'create' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('create')}
          >
            {t('anpTabCreate')}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'app-update' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('app-update')}
          >
            <Smartphone size={14} style={{ marginRight: 4 }} />
            {t('anpTabAppUpdate')}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'stories' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('stories')}
          >
            <Image size={14} style={{ marginRight: 4 }} />
            {t('anpTabStories')}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'history' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('history')}
          >
            {t('anpTabHistory')}
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'portfolio-email' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('portfolio-email')}
          >
            <Mail size={14} style={{ marginRight: 4 }} />
            {t('anpTabPortfolioEmail')}
          </button>
        </div>
      </div>

      {/* Create Tab */}
      {activeTab === 'create' && (
        <form onSubmit={handleSubmit} style={styles.form}>
          {success && <div style={styles.success}>{success}</div>}
          {error && <div style={styles.errorMsg}>{error}</div>}

          {/* Type Selection */}
          <div style={styles.section}>
            <label style={styles.label}>{t('anpNotificationType')}</label>
            <div style={styles.typeGrid}>
              {notificationTypes.map(type => {
                const Icon = type.icon;
                const isSelected = formData.type === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    style={{
                      ...styles.typeBtn,
                      borderColor: isSelected ? type.color : '#eee',
                      background: isSelected ? `${type.color}15` : 'white'
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, type: type.value as NotificationFormData['type'] }))}
                  >
                    <Icon size={20} color={type.color} />
                    <span style={styles.typeBtnLabel}>{t(type.labelKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t('anpTitleEnRequired')}</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Upcoming Webinar: Dubai Market Update"
                style={styles.input}
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t('anpTitleDeLabel')}</label>
              <input
                type="text"
                value={formData.titleDe}
                onChange={e => setFormData(prev => ({ ...prev, titleDe: e.target.value }))}
                placeholder="e.g., Kommendes Webinar: Dubai Marktupdate"
                style={styles.input}
              />
            </div>
          </div>

          {/* Message */}
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t('anpMessageEnRequired')}</label>
              <textarea
                value={formData.message}
                onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Join us for an exclusive market analysis..."
                style={styles.textarea}
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>{t('anpMessageDeLabel')}</label>
              <textarea
                value={formData.messageDe}
                onChange={e => setFormData(prev => ({ ...prev, messageDe: e.target.value }))}
                placeholder="Begleiten Sie uns für eine exklusive Marktanalyse..."
                style={styles.textarea}
              />
            </div>
          </div>

          {/* Priority */}
          <div style={styles.section}>
            <label style={styles.label}>{t('anpPriority')}</label>
            <div style={styles.priorityRow}>
              {(['low', 'medium', 'high'] as const).map(p => (
                <button
                  key={p}
                  type="button"
                  style={{
                    ...styles.priorityBtn,
                    ...(formData.priority === p ? styles[`priority${p.charAt(0).toUpperCase() + p.slice(1)}` as keyof typeof styles] as React.CSSProperties : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                >
                  {t(priorityLabelKeys[p])}
                </button>
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div style={styles.section}>
            <label style={styles.label}>{t('anpSendTo')}</label>
            <div style={styles.audienceRow}>
              <button
                type="button"
                style={{
                  ...styles.audienceBtn,
                  ...(formData.targetAudience === 'all' ? styles.audienceBtnActive : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, targetAudience: 'all' }))}
              >
                <Users size={18} />
                {t('anpAllInvestors')}
              </button>
              <button
                type="button"
                style={{
                  ...styles.audienceBtn,
                  ...(formData.targetAudience === 'specific' ? styles.audienceBtnActive : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, targetAudience: 'specific' }))}
              >
                <User size={18} />
                {t('anpSpecificInvestor')}
              </button>
            </div>

            {formData.targetAudience === 'specific' && (
              <select
                value={formData.investorId}
                onChange={e => setFormData(prev => ({ ...prev, investorId: e.target.value }))}
                style={styles.select}
                required
              >
                <option value="">{t('anpSelectInvestor')}</option>
                {investors.map(inv => (
                  <option key={inv.id} value={inv.id}>{inv.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Action URL */}
          <div style={styles.section}>
            <label style={styles.label}>{t('anpActionLink')}</label>
            <div style={styles.audienceRow}>
              <button
                type="button"
                style={{
                  ...styles.actionBtn,
                  ...(formData.actionType === 'none' ? styles.actionBtnActive : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, actionType: 'none', actionUrl: '' }))}
              >
                {t('anpActionNone')}
              </button>
              <button
                type="button"
                style={{
                  ...styles.actionBtn,
                  ...(formData.actionType === 'internal' ? styles.actionBtnActive : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, actionType: 'internal' }))}
              >
                {t('anpActionAppTab')}
              </button>
              <button
                type="button"
                style={{
                  ...styles.actionBtn,
                  ...(formData.actionType === 'external' ? styles.actionBtnActive : {})
                }}
                onClick={() => setFormData(prev => ({ ...prev, actionType: 'external' }))}
              >
                {t('anpActionExternalUrl')}
              </button>
            </div>

            {formData.actionType === 'internal' && (
              <select
                value={formData.actionUrl}
                onChange={e => setFormData(prev => ({ ...prev, actionUrl: e.target.value }))}
                style={styles.select}
              >
                <option value="">{t('anpSelectTab')}</option>
                <option value="/portfolio">{t('portfolio')}</option>
                <option value="/properties">{t('properties')}</option>
                <option value="/documents">{t('documents')}</option>
                <option value="/marketplace">{t('anpLinkMarketplace')}</option>
              </select>
            )}

            {formData.actionType === 'external' && (
              <input
                type="url"
                value={formData.actionUrl}
                onChange={e => setFormData(prev => ({ ...prev, actionUrl: e.target.value }))}
                placeholder="https://zoom.us/j/123456789"
                style={{...styles.input, marginTop: 12}}
              />
            )}
          </div>

          {/* Submit */}
          <button 
            type="submit" 
            style={{
              ...styles.submitBtn,
              opacity: loading || !formData.title || !formData.message ? 0.6 : 1
            }}
            disabled={loading || !formData.title || !formData.message}
          >
            {loading ? (
              <RefreshCw size={18} className="spin" />
            ) : (
              <Send size={18} />
            )}
            {loading ? t('anpSending') : t('anpSendNotification')}
          </button>
        </form>
      )}

      {/* App Update Tab */}
      {activeTab === 'app-update' && (
        <div style={appUpdateStyles.wrapper}>
          {updateSuccess && <div style={styles.success}>{updateSuccess}</div>}
          {updateError && <div style={styles.errorMsg}>{updateError}</div>}

          {/* Professional Banner Preview */}
          <div style={appUpdateStyles.previewCard}>
            <div style={appUpdateStyles.previewLabel}>
              <Zap size={14} color="#D6B160" />
              <span>{t('anpNotificationPreview')}</span>
            </div>

            {/* Banner Image */}
            <div style={appUpdateStyles.bannerContainer}>
              <img
                src="/app-update-banner.png"
                alt={t('anpAppUpdateBannerAlt')}
                style={appUpdateStyles.bannerImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>

            {/* Preview Content */}
            <div style={appUpdateStyles.previewContent}>
              <div style={appUpdateStyles.previewBadge}>
                <Megaphone size={12} />
                {t('anpAppUpdateBadge')}
              </div>
              <h3 style={appUpdateStyles.previewTitle}>
                {updateForm.titleEn || 'New App Update Available!'}
              </h3>
              {updateForm.version && (
                <div style={appUpdateStyles.versionBadge}>v{updateForm.version}</div>
              )}
              <div style={appUpdateStyles.previewFeatures}>
                {updateForm.features.filter(f => f.trim()).map((f, i) => (
                  <div key={i} style={appUpdateStyles.featureItem}>
                    <CheckCircle size={14} color="#50C878" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div style={appUpdateStyles.formCard}>
            <h3 style={appUpdateStyles.formTitle}>{t('anpUpdateDetails')}</h3>

            {/* Version */}
            <div style={styles.section}>
              <label style={styles.label}>{t('anpAppVersion')}</label>
              <input
                type="text"
                value={updateForm.version}
                onChange={e => setUpdateForm(prev => ({ ...prev, version: e.target.value }))}
                placeholder={t('anpAppVersionPh')}
                style={styles.input}
              />
            </div>

            {/* Titles */}
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpTitleEnPlain')}</label>
                <input
                  type="text"
                  value={updateForm.titleEn}
                  onChange={e => setUpdateForm(prev => ({ ...prev, titleEn: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpTitleDeLabel')}</label>
                <input
                  type="text"
                  value={updateForm.titleDe}
                  onChange={e => setUpdateForm(prev => ({ ...prev, titleDe: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>

            {/* What's New Features */}
            <div style={styles.section}>
              <label style={styles.label}>{t('anpWhatsNew')}</label>
              {updateForm.features.map((feature, idx) => (
                <input
                  key={idx}
                  type="text"
                  value={feature}
                  onChange={e => {
                    const updated = [...updateForm.features];
                    updated[idx] = e.target.value;
                    setUpdateForm(prev => ({ ...prev, features: updated }));
                  }}
                  placeholder={
                    idx === 0 ? t('anpFeaturePh1')
                    : idx === 1 ? t('anpFeaturePh2')
                    : t('anpFeaturePh3')
                  }
                  style={{ ...styles.input, marginBottom: 8 }}
                />
              ))}
            </div>

            {/* Custom Message Override */}
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpCustomMessageEn')}</label>
                <textarea
                  value={updateForm.messageEn}
                  onChange={e => setUpdateForm(prev => ({ ...prev, messageEn: e.target.value }))}
                  placeholder={t('anpLeaveEmptyAutoLong')}
                  style={styles.textarea}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpCustomMessageDe')}</label>
                <textarea
                  value={updateForm.messageDe}
                  onChange={e => setUpdateForm(prev => ({ ...prev, messageDe: e.target.value }))}
                  placeholder={t('anpLeaveEmptyAutoShort')}
                  style={styles.textarea}
                />
              </div>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendAppUpdate}
              disabled={updateLoading}
              style={{
                ...appUpdateStyles.sendButton,
                opacity: updateLoading ? 0.6 : 1,
              }}
            >
              {updateLoading ? (
                <RefreshCw size={20} className="spin" />
              ) : (
                <Send size={20} />
              )}
              {updateLoading ? t('anpSendingToAllInvestors') : t('anpNotifyAllInvestors')}
            </button>

            <p style={appUpdateStyles.sendHint}>
              <Users size={14} />
              {t('anpHighPriorityHint')}
            </p>
          </div>
        </div>
      )}

      {/* Stories Tab */}
      {activeTab === 'stories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Create Story Form */}
          <div style={styles.form}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>
              <Plus size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              {t('anpCreateNewStory')}
            </h3>

            {storySuccess && <div style={styles.success}>{storySuccess}</div>}
            {storyError && <div style={styles.errorMsg}>{storyError}</div>}

            {/* Category */}
            <div style={styles.section}>
              <label style={styles.label}>{t('anpStoryCategory')}</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {storyCategoryOptions.map(cat => {
                  const Icon = cat.icon;
                  const isSelected = storyForm.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        border: `2px solid ${isSelected ? cat.color : '#eee'}`,
                        borderRadius: 8,
                        background: isSelected ? `${cat.color}15` : 'white',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: isSelected ? 600 : 400,
                      }}
                      onClick={() => setStoryForm(prev => ({ ...prev, category: cat.value }))}
                    >
                      <Icon size={16} color={cat.color} />
                      {t(cat.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpTitleEnRequired')}</label>
                <input
                  type="text"
                  value={storyForm.title}
                  onChange={e => setStoryForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Seagate 2BR - Ready to Move In"
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpTitleDeLabel')}</label>
                <input
                  type="text"
                  value={storyForm.titleDe}
                  onChange={e => setStoryForm(prev => ({ ...prev, titleDe: e.target.value }))}
                  placeholder="e.g., Seagate 2-Zimmer - Bezugsfertig"
                  style={styles.input}
                />
              </div>
            </div>

            {/* Image URL */}
            <div style={styles.section}>
              <label style={styles.label}>{t('anpImageUrl')}</label>
              <input
                type="text"
                value={storyForm.imageUrl}
                onChange={e => setStoryForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                placeholder={t('anpImageUrlPh')}
                style={styles.input}
              />
              {storyForm.imageUrl && (
                <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', maxWidth: 200 }}>
                  <img
                    src={storyForm.imageUrl}
                    alt={t('anpPreview')}
                    style={{ width: '100%', height: 120, objectFit: 'cover' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpDescriptionEn')}</label>
                <textarea
                  value={storyForm.description}
                  onChange={e => setStoryForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Spacious 2-bedroom apartment with marina views..."
                  style={styles.textarea}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpDescriptionDe')}</label>
                <textarea
                  value={storyForm.descriptionDe}
                  onChange={e => setStoryForm(prev => ({ ...prev, descriptionDe: e.target.value }))}
                  placeholder="Geräumige 2-Zimmer-Wohnung mit Blick auf den Yachthafen..."
                  style={styles.textarea}
                />
              </div>
            </div>

            {/* Property details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <Building2 size={12} style={{ marginRight: 4 }} /> {t('project')}
                </label>
                <input
                  type="text"
                  value={storyForm.project}
                  onChange={e => setStoryForm(prev => ({ ...prev, project: e.target.value }))}
                  placeholder="Seagate"
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <DollarSign size={12} style={{ marginRight: 4 }} /> {t('anpPrice')}
                </label>
                <input
                  type="text"
                  value={storyForm.price}
                  onChange={e => setStoryForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="AED 2,400,000"
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <BedDouble size={12} style={{ marginRight: 4 }} /> {t('bedrooms')}
                </label>
                <input
                  type="text"
                  value={storyForm.bedrooms}
                  onChange={e => setStoryForm(prev => ({ ...prev, bedrooms: e.target.value }))}
                  placeholder="2 BR"
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <MapPin size={12} style={{ marginRight: 4 }} /> {t('location')}
                </label>
                <input
                  type="text"
                  value={storyForm.location}
                  onChange={e => setStoryForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Dubai, UAE"
                  style={styles.input}
                />
              </div>
            </div>

            {/* CTA Link */}
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpButtonLabel')}</label>
                <input
                  type="text"
                  value={storyForm.linkLabel}
                  onChange={e => setStoryForm(prev => ({ ...prev, linkLabel: e.target.value }))}
                  placeholder={t('anpButtonLabelPh')}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('anpButtonUrl')}</label>
                <input
                  type="text"
                  value={storyForm.linkUrl}
                  onChange={e => setStoryForm(prev => ({ ...prev, linkUrl: e.target.value }))}
                  placeholder="https://..."
                  style={styles.input}
                />
              </div>
            </div>

            {/* Expiry */}
            <div style={styles.section}>
              <label style={styles.label}>{t('anpStoryDuration')}</label>
              <select
                value={storyForm.expiresInHours}
                onChange={e => setStoryForm(prev => ({ ...prev, expiresInHours: e.target.value }))}
                style={styles.select}
              >
                <option value="24">{t('anpDuration24h')}</option>
                <option value="48">{t('anpDuration48h')}</option>
                <option value="72">{t('anpDuration3d')}</option>
                <option value="168">{t('anpDuration7d')}</option>
                <option value="336">{t('anpDuration14d')}</option>
                <option value="720">{t('anpDuration30d')}</option>
              </select>
            </div>

            {/* Submit */}
            <button
              onClick={handleCreateStory}
              disabled={storyLoading || !storyForm.title || !storyForm.imageUrl}
              style={{
                ...styles.submitBtn,
                opacity: storyLoading || !storyForm.title || !storyForm.imageUrl ? 0.6 : 1,
              }}
            >
              {storyLoading ? <RefreshCw size={18} className="spin" /> : <Send size={18} />}
              {storyLoading ? t('anpPublishing') : t('anpPublishStory')}
            </button>
          </div>

          {/* Existing Stories List */}
          <div style={styles.historyList}>
            <div style={styles.historyHeader}>
              <h3 style={{ margin: 0 }}>{t('anpActiveStories')} ({existingStories.length})</h3>
              <button onClick={loadStories} style={styles.refreshBtn}>
                <RefreshCw size={16} /> {t('anpRefresh')}
              </button>
            </div>

            {existingStories.length === 0 ? (
              <div style={styles.empty}>{t('anpNoStories')}</div>
            ) : (
              existingStories.map(story => {
                const isExpired = story.expiresAt && new Date(
                  story.expiresAt._seconds ? story.expiresAt._seconds * 1000 : story.expiresAt
                ) < new Date();
                return (
                  <div key={story.id} style={{
                    ...styles.historyItem,
                    opacity: isExpired ? 0.5 : 1,
                  }}>
                    <div style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', flexShrink: 0, marginRight: 12 }}>
                      <img
                        src={story.imageUrl}
                        alt={story.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                    <div style={styles.historyContent}>
                      <strong style={{ fontSize: 14 }}>
                        {story.title}
                        {isExpired && <span style={{ color: '#E74C3C', fontSize: 11, marginLeft: 8 }}>{t('anpExpiredBadge')}</span>}
                      </strong>
                      <div style={styles.historyMeta}>
                        <span>{story.category}</span>
                        {story.project && <span>{story.project}</span>}
                        {story.price && <span>{story.price}</span>}
                        <span>
                          <Eye size={11} style={{ marginRight: 2 }} />
                          {story.viewCount || 0} {t('anpViews')}
                        </span>
                        {story.createdAt && (
                          <span>{new Date(
                            story.createdAt._seconds ? story.createdAt._seconds * 1000 : story.createdAt
                          ).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteStory(story.id)} style={styles.deleteBtn}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div style={styles.historyList}>
          <div style={styles.historyHeader}>
            <h3 style={{margin: 0}}>{t('anpRecentNotifications')}</h3>
            <button onClick={loadNotifications} style={styles.refreshBtn}>
              <RefreshCw size={16} /> {t('anpRefresh')}
            </button>
          </div>

          {recentNotifications.length === 0 ? (
            <div style={styles.empty}>{t('anpNoNotifications')}</div>
          ) : (
            recentNotifications.map(n => (
              <div key={n.id} style={styles.historyItem}>
                <div style={styles.historyContent}>
                  <strong style={{fontSize: 14}}>{n.title}</strong>
                  <p style={{margin: '4px 0', color: '#666', fontSize: 13}}>{n.message}</p>
                  <div style={styles.historyMeta}>
                    <span>{n.investorId === 'all' ? t('anpAllInvestors') : n.investorId}</span>
                    <span>{new Date(n.timestamp).toLocaleString()}</span>
                    <span style={{ color: n.read ? '#999' : '#D6B160' }}>
                      {n.read ? t('anpRead') : t('anpUnread')}
                    </span>
                  </div>
                </div>
                <button onClick={() => handleDelete(n.id)} style={styles.deleteBtn}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Portfolio Email Tab */}
      {activeTab === 'portfolio-email' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#1a1a2e' }}>{t('anpPeTitle')}</h3>
          <p style={{ margin: '0 0 20px', color: '#888', fontSize: 13 }}>
            {t('anpPeSubtitle')}
          </p>

          {portfolioEmailStatus && (
            <div style={{
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              background: portfolioEmailStatus.type === 'success' ? '#e8f5e9' : '#ffebee',
              color: portfolioEmailStatus.type === 'success' ? '#2e7d32' : '#c62828',
              fontSize: 14
            }}>
              {portfolioEmailStatus.message}
            </div>
          )}

          {/* Single Investor Section */}
          <div style={{ marginBottom: 24, padding: 20, background: '#faf8f3', borderRadius: 10, borderLeft: '4px solid #D6B160' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#333', fontSize: 13 }}>
              {t('anpPeSendSpecific')}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={portfolioEmailInvestorId}
                onChange={(e) => setPortfolioEmailInvestorId(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: 200,
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="">{t('anpPeSelectInvestor')}</option>
                {portfolioInvestorList.map(inv => (
                  <option key={inv.id} value={inv.id}>{inv.name} ({inv.email || inv.id})</option>
                ))}
              </select>
              <button
                onClick={handlePortfolioEmailPreview}
                disabled={!portfolioEmailInvestorId || portfolioEmailLoading}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: 8,
                  background: portfolioEmailInvestorId ? '#1a1a2e' : '#ccc',
                  color: 'white',
                  cursor: portfolioEmailInvestorId ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap'
                }}
              >
                {portfolioEmailLoading ? <Loader size={14} className="spin" /> : <Eye size={14} />}
                {t('anpPreview')}
              </button>
              <button
                onClick={handlePortfolioEmailSend}
                disabled={!portfolioEmailInvestorId || portfolioEmailSending}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: 8,
                  background: portfolioEmailInvestorId ? '#D6B160' : '#ccc',
                  color: portfolioEmailInvestorId ? '#1a1a2e' : '#666',
                  cursor: portfolioEmailInvestorId ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap'
                }}
              >
                {portfolioEmailSending ? <Loader size={14} className="spin" /> : <Send size={14} />}
                {t('anpSendEmail')}
              </button>
            </div>

            {portfolioEmailSummary && (
              <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#666' }}>
                  <strong>{portfolioEmailSummary.totalUnits}</strong> {t('anpPeProperties')}
                </span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {t('anpPeTotalValue')}: <strong>AED {portfolioEmailSummary.totalValue?.toLocaleString()}</strong>
                </span>
                <span style={{ fontSize: 13, color: '#2e7d32' }}>
                  ROI: <strong>+{portfolioEmailSummary.totalROI?.toFixed(1)}%</strong>
                </span>
              </div>
            )}
          </div>

          {/* Bulk Send Section */}
          <div style={{ marginBottom: 24, padding: 20, background: '#f5f5f5', borderRadius: 10, borderLeft: '4px solid #4caf50' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#333', fontSize: 13 }}>
              {t('anpPeSendAll')}
            </label>
            <p style={{ margin: '0 0 12px', color: '#666', fontSize: 13 }}>
              {t('anpPeSendAllDesc')}
            </p>
            <button
              onClick={handlePortfolioEmailSendAll}
              disabled={portfolioEmailBulkSending}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: 8,
                background: '#4caf50',
                color: 'white',
                cursor: portfolioEmailBulkSending ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {portfolioEmailBulkSending ? <Loader size={14} className="spin" /> : <Users size={14} />}
              {portfolioEmailBulkSending ? t('anpPeSendingAll') : t('anpPeSendAll')}
            </button>
          </div>

          {/* Auto Schedule Info */}
          <div style={{ padding: 16, background: '#e3f2fd', borderRadius: 8, borderLeft: '4px solid #1976d2', marginBottom: 24 }}>
            <p style={{ margin: 0, color: '#1565c0', fontSize: 13 }}>
              <strong>{t('anpPeAutoTitle')}:</strong> {t('anpPeAutoBody')}
            </p>
          </div>

          {/* Email Preview */}
          {portfolioEmailPreview && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0, color: '#333' }}>{t('anpPeEmailPreview')}</h4>
                <button
                  onClick={() => {
                    const w = window.open('', '_blank');
                    if (w) {
                      w.document.write(portfolioEmailPreview);
                      w.document.close();
                    }
                  }}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#555'
                  }}
                >
                  {t('anpPeOpenInNewTab')}
                </button>
              </div>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                overflow: 'hidden',
                maxHeight: 600,
                overflowY: 'auto'
              }}>
                <iframe
                  srcDoc={portfolioEmailPreview}
                  style={{ width: '100%', height: 600, border: 'none' }}
                  title={t('anpPeIframeTitle')}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: 24,
    fontFamily: "'Montserrat', -apple-system, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: '1px solid #eee',
    flexWrap: 'wrap',
    gap: 12,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  tabs: {
    display: 'flex',
    gap: 8,
  },
  tab: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    borderRadius: 8,
    background: 'white',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  tabActive: {
    background: '#D6B160',
    color: 'white',
    borderColor: '#D6B160',
  },
  form: {
    background: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  section: {
    marginBottom: 24,
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: 8,
    color: '#333',
    fontSize: 13,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 24,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    padding: 12,
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
  },
  textarea: {
    padding: 12,
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    minHeight: 80,
    resize: 'vertical' as const,
  },
  select: {
    padding: 12,
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    marginTop: 12,
    width: '100%',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
  },
  typeBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: 14,
    border: '2px solid #eee',
    borderRadius: 10,
    background: 'white',
    cursor: 'pointer',
  },
  typeBtnLabel: {
    fontSize: 11,
    fontWeight: 500,
  },
  priorityRow: {
    display: 'flex',
    gap: 10,
  },
  priorityBtn: {
    flex: 1,
    padding: 12,
    border: '2px solid #eee',
    borderRadius: 8,
    background: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  priorityLow: {
    borderColor: '#95A5A6',
    background: '#f8f9fa',
  },
  priorityMedium: {
    borderColor: '#F39C12',
    background: '#fef9e7',
  },
  priorityHigh: {
    borderColor: '#E74C3C',
    background: '#fdedec',
  },
  audienceRow: {
    display: 'flex',
    gap: 10,
  },
  audienceBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 12,
    border: '2px solid #eee',
    borderRadius: 8,
    background: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  audienceBtnActive: {
    borderColor: '#D6B160',
    background: '#fef9e7',
  },
  actionBtn: {
    flex: 1,
    padding: 10,
    border: '2px solid #eee',
    borderRadius: 8,
    background: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  actionBtnActive: {
    borderColor: '#D6B160',
    background: '#fef9e7',
  },
  submitBtn: {
    width: '100%',
    padding: 16,
    background: '#D6B160',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  success: {
    padding: 12,
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: 8,
    marginBottom: 16,
    fontWeight: 500,
  },
  errorMsg: {
    padding: 12,
    background: '#ffebee',
    color: '#c62828',
    borderRadius: 8,
    marginBottom: 16,
  },
  historyList: {
    background: 'white',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: 6,
    background: 'white',
    cursor: 'pointer',
    fontSize: 13,
  },
  historyItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: 14,
    borderBottom: '1px solid #f0f0f0',
  },
  historyContent: {
    flex: 1,
  },
  historyMeta: {
    display: 'flex',
    gap: 16,
    marginTop: 8,
    fontSize: 11,
    color: '#888',
  },
  deleteBtn: {
    padding: 8,
    border: 'none',
    background: 'transparent',
    color: '#ccc',
    cursor: 'pointer',
  },
  empty: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#999',
  },
};

const appUpdateStyles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  previewCard: {
    background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 50%, #0d0d0d 100%)',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    border: '1px solid rgba(214, 177, 96, 0.2)',
  },
  previewLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    background: 'rgba(214, 177, 96, 0.1)',
    color: '#D6B160',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
  },
  bannerContainer: {
    width: '100%',
    maxHeight: 400,
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#0a0a1a',
  },
  bannerImage: {
    width: '100%',
    height: 'auto',
    objectFit: 'cover' as const,
    display: 'block',
  },
  previewContent: {
    padding: '20px 24px 24px',
  },
  previewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    background: 'rgba(214, 177, 96, 0.15)',
    color: '#D6B160',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 12,
  },
  previewTitle: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: 700,
    margin: '0 0 8px',
    fontFamily: "'Montserrat', sans-serif",
  },
  versionBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    background: 'rgba(80, 200, 120, 0.15)',
    color: '#50C878',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 16,
  },
  previewFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 12,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#ccc',
    fontSize: 14,
  },
  formCard: {
    background: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  formTitle: {
    fontSize: 18,
    fontWeight: 700,
    margin: '0 0 20px',
    color: '#1a1a1a',
  },
  sendButton: {
    width: '100%',
    padding: 18,
    background: 'linear-gradient(135deg, #D6B160 0%, #c49b3d 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxShadow: '0 4px 16px rgba(214, 177, 96, 0.3)',
    fontFamily: "'Montserrat', sans-serif",
  },
  sendHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
    color: '#999',
    fontSize: 12,
  },
};

export default AdminNotificationPanel;